import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';
import { Header } from '../components/Header';
import { AdminSidebar } from '../components/AdminSidebar';
import { HomePage } from '../pages/HomePage';
import { ProfilePage } from '../pages/ProfilePage';
import { SettingsPage } from '../pages/SettingsPage';
import { AuthPage } from '../pages/AuthPage';
import { AccessDeniedPage } from '../pages/AccessDeniedPage';
import { UserManagementPage } from '../pages/UserManagementPage';
import TrainingManagementPage from '../pages/TrainingManagementPage';
import CourseEditorPage from '../pages/CourseEditorPage';
import CourseAnalyticsPage from '../pages/CourseAnalyticsPage';
import CertificatesManagementPage from '../pages/CertificatesManagementPage';
import ModerationQueuePage from '../pages/ModerationQueuePage';
import { SafetyContactsAdminPage } from '../pages/SafetyContactsAdminPage';
import NotAFitAuditPage from '../pages/NotAFitAuditPage';
import CaregiverVerificationQueuePage from '../pages/CaregiverVerificationQueuePage';
import CredentialReviewQueuePage from '../pages/CredentialReviewQueuePage';
import IncidentResponsePage from '../pages/IncidentResponsePage';
import DataAccessRequestQueuePage from '../pages/DataAccessRequestQueuePage';
import StateAgencyMappingsPage from '../pages/StateAgencyMappingsPage';
import ServiceAreasPage from '../pages/ServiceAreasPage';
import PremiumPlacementsPage from '../pages/PremiumPlacementsPage';
import FeatureFlagsPage from '../pages/FeatureFlagsPage';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';

// Protected route component for admin users
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useApp();
  
  // Show loading while checking admin access
  if (state.isLoading || !state.adminAccessChecked) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Checking admin access...</div>
      </Box>
    );
  }

  // Redirect to auth if not authenticated or not admin
  if (!authService.isAuthenticated() || !state.isAdmin) {
    return <Navigate to="/admin/auth" replace />;
  }
  
  return <>{children}</>;
};

// Public route component (redirects to admin dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useApp();
  
  // Show loading while checking admin access
  if (state.isLoading || !state.adminAccessChecked) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Checking admin access...</div>
      </Box>
    );
  }
  
  if (authService.isAuthenticated() && state.isAdmin) {
    return <Navigate to="/admin" replace />;
  }
  
  return <>{children}</>;
};

export const AppNavigator: React.FC = () => {
  const { state } = useApp();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleMenuClick = () => {
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const handleProfileClick = () => {
    // Handle profile click - navigate to admin profile page
    window.location.href = '/admin/profile';
  };

  return (
    <Router>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Only show header for authenticated admin users */}
        {authService.isAuthenticated() && state.isAdmin && (
          <>
            <Header
              onMenuClick={handleMenuClick}
              onProfileClick={handleProfileClick}
              title="ShareGiver Admin"
            />
            <AdminSidebar
              open={sidebarOpen}
              onClose={handleSidebarClose}
            />
          </>
        )}
        
        <Box component="main" sx={{ flexGrow: 1, p: authService.isAuthenticated() && state.isAdmin ? 0 : 0 }}>
          <Routes>
            {/* Public routes */}
            <Route 
              path="/admin/auth" 
              element={
                <PublicRoute>
                  <AuthPage />
                </PublicRoute>
              } 
            />
            <Route 
              path="/admin/access-denied" 
              element={<AccessDeniedPage />} 
            />
            
            {/* Protected admin routes */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/profile" 
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/settings" 
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <ProtectedRoute>
                  <UserManagementPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Training Management routes */}
            <Route 
              path="/admin/training" 
              element={
                <ProtectedRoute>
                  <TrainingManagementPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/training/courses/:courseId" 
              element={
                <ProtectedRoute>
                  <CourseEditorPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/training/courses/:courseId/analytics" 
              element={
                <ProtectedRoute>
                  <CourseAnalyticsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/training/certificates" 
              element={
                <ProtectedRoute>
                  <CertificatesManagementPage />
                </ProtectedRoute>
              } 
            />

            {/* FEAT-042 — Not a Fit Audit (MVP-8.8) */}
            <Route
              path="/admin/not-a-fit"
              element={
                <ProtectedRoute>
                  <NotAFitAuditPage />
                </ProtectedRoute>
              }
            />

            {/* FEAT-044 Safety Contacts Admin (MVP-8.10a) */}
            <Route
              path="/admin/safety-contacts"
              element={
                <ProtectedRoute>
                  <SafetyContactsAdminPage />
                </ProtectedRoute>
              }
            />

            {/* FEAT-041 Moderation Queue (MVP-8.5/8.6/8.7) */}
            <Route
              path="/admin/moderation"
              element={
                <ProtectedRoute>
                  <ModerationQueuePage />
                </ProtectedRoute>
              }
            />

            {/* FEAT-046 Credential Review Queue (MVP-18.20/18.21) */}
            <Route
              path="/admin/credential-review"
              element={
                <ProtectedRoute>
                  <CredentialReviewQueuePage />
                </ProtectedRoute>
              }
            />

            {/* FEAT-039 Caregiver Verification Queue (MVP-8.1, MVP-18.14) */}
            <Route
              path="/admin/caregiver-verification"
              element={
                <ProtectedRoute>
                  <CaregiverVerificationQueuePage />
                </ProtectedRoute>
              }
            />

            {/* FEAT-047 State Agency Mappings (MVP-8.19, MVP-8.35, MVP-18.41) */}
            <Route
              path="/admin/state-agency-mappings"
              element={
                <ProtectedRoute>
                  <StateAgencyMappingsPage />
                </ProtectedRoute>
              }
            />

            {/* FEAT-063 Service Areas (MVP-18.6, MVP-18.25) */}
            <Route
              path="/admin/service-areas"
              element={
                <ProtectedRoute>
                  <ServiceAreasPage />
                </ProtectedRoute>
              }
            />

            {/* FEAT-065 Premium Placements (MVP-18.7) */}
            <Route
              path="/admin/premium-placements"
              element={
                <ProtectedRoute>
                  <PremiumPlacementsPage />
                </ProtectedRoute>
              }
            />

            {/* FEAT-059 Feature Flags (MVP-18.37) */}
            <Route
              path="/admin/feature-flags"
              element={
                <ProtectedRoute>
                  <FeatureFlagsPage />
                </ProtectedRoute>
              }
            />

            {/* FEAT-077 Incident Response (MVP-18.45) */}
            <Route
              path="/admin/incidents"
              element={
                <ProtectedRoute>
                  <IncidentResponsePage />
                </ProtectedRoute>
              }
            />

            {/* FEAT-078 Data Access Queue (MVP-18.46) */}
            <Route
              path="/admin/data-access-requests"
              element={
                <ProtectedRoute>
                  <DataAccessRequestQueuePage />
                </ProtectedRoute>
              }
            />

            {/* Default redirects */}
            <Route path="/" element={<Navigate to="/admin" replace />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </Box>
      </Box>
    </Router>
  );
};
